import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, HStack, Tag } from '@chakra-ui/react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { StatusTag } from 'components/StatusTag';
import SystemPartnerCard from 'components/SystemPartnerCard';
import { useUserContext } from 'context/UserContextProvider';
import { dateTransformerNoTimezone } from 'helpers/dateTransformer';
import WorkshopServiceRecordOverviewCard from 'pages/serviceRecord/WorkshopServiceRecordsOverviewCard';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosWarning } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';

import ActivityLogCard from '../../components/ActivityLogCard';
import useSystemPartnerQuery from '../../hooks/useSystemPartnerQuery';
import useWorkshopQuery from '../../hooks/useWorkshopQuery';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';
import WorkshopServiceHistoryOverviewCard from '../serviceHistory/WorkshopServiceHistoryOverviewCard';
import { AccountsTable } from './AccountsTable';
import AddAccountModal from './AddAccountModal';
import DangerZoneCard from './dangerZone/DangerZoneCard';
import FreeTrailDetailsCard from './FreeTrialDetailsCard';
import ScheduledPlanChangesCard from './ScheduledPlanChangesCard';
import SubscriptionDetailsCard from './SubscriptionDetailsCard';
import WorkshopDataUpdateRequestCard from './WorkshopDataUpdateRequestCard';
import WorkshopDetailsCard from './WorkshopDetailsCard';
import WorkshopHandwerkskarteCard from './WorkshopHandwerkskarteCard';
import WorkshopUserOverviewCard from './WorkshopUsersOverviewCard';
import WorkshopVerificationOverviewCard from './WorkshopVerificationOverviewCard';

const WorkshopDetailsPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const workshopId = Number(id);
  const { t } = useTranslation();
  const resourceKey = 'workshop';
  const { isMember, statusCodes, user } = useUserContext();
  const workshopQuery = useWorkshopQuery(workshopId);
  const [focusInput, setFocusInput] = useState(false);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const systemPartnerQuery = useSystemPartnerQuery();
  const workshopHasSystemPartner = systemPartnerQuery.data?.find((systemPartner) => {
    return systemPartner.id === workshopQuery.data?.systempartner_id;
  });

  const hasSystemPartnerUsers = workshopQuery.data?.users.some((user) => {
    return user.systempartner_id !== null;
  });

  const systemPartners = systemPartnerQuery.data?.map((partner) => partner.name);

  const canManageFreeTrial =
    user?.permissions.includes('workshops:extend-trial') &&
    (workshopQuery.data?.in_free_trial || (workshopQuery.data?.status === 900 && workshopQuery.data?.subscription === null));
  const canAddAccount = isMember('accounts:add');
  if (isNaN(workshopId) || workshopId <= 0) {
    navigate('/404', { replace: true });
    return null;
  }

  if (workshopQuery.isLoading || systemPartnerQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (workshopQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  const isInFreeTrial = workshopQuery.data?.in_free_trial;
  const subscriptionStatus = workshopQuery.data?.subscription?.status;
  const cancelledSubscription = subscriptionStatus == 300 && !isInFreeTrial;
  const workshopActiveUntil = workshopQuery.data?.subscription?.ended_at;
  const hasSubscription = workshopQuery.data?.subscription?.id != null;

  return (
    <ContentLayout>
      <PageMetatags title={t('pages:workshops.details.meta.title')} />
      {!hasSubscription && workshopHasSystemPartner && <SystemPartnerCard name={workshopHasSystemPartner?.name} />}
      {hasSubscription && hasSystemPartnerUsers && (
        <Alert bgColor="purple.100">
          <AlertIcon color="purple.500" as={IoIosWarning} />
          <Box>
            <AlertTitle>{t(`workshop:system_partner.uses_cp_and_partner_alert.title`)}</AlertTitle>
            <AlertDescription>
              <Trans
                i18nKey="workshop:system_partner.uses_cp_and_partner_alert.description"
                values={{
                  partners: systemPartners?.join(', '),
                }}
              />
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <HStack bgColor="white" p="6" borderRadius="lg" boxShadow="md">
        {workshopQuery.isSuccess && <StatusTag currentStatus={workshopQuery.data.status} resourceKey="workshop" />}
        {cancelledSubscription && (
          <HStack>
            <Tag colorScheme="orange" variant="outline" size="sm" paddingBlock={2} mt={1} textTransform="uppercase">
              {t('workshop:end_contract.tags.contract_ended')}
            </Tag>
            <Tag colorScheme="orange" variant="outline" size="sm" paddingBlock={2} mt={1} textTransform="uppercase">
              <Trans i18nKey="workshop:end_contract.tags.active_until" values={{ date: dateTransformerNoTimezone(workshopActiveUntil) }} />
            </Tag>
          </HStack>
        )}
      </HStack>

      <WorkshopDetailsCard workshopId={workshopId} />

      {canManageFreeTrial && <FreeTrailDetailsCard workshopId={workshopId} />}

      <ScheduledPlanChangesCard workshopId={workshopId} />

      <SubscriptionDetailsCard workshopId={workshopId} />

      <WorkshopUserOverviewCard workshopId={workshopId} />

      <WorkshopVerificationOverviewCard
        autoFocus={false}
        isLoading={workshopQuery.isLoading}
        // @ts-ignore
        workshopVerificationCodes={statusCodes?.workshop_verification_request}
        // @ts-ignore
        workshop={workshopQuery.data}
      />
      {workshopQuery.data?.country === 'DE' && <WorkshopHandwerkskarteCard workshopId={workshopId} workshop={workshopQuery.data} />}
      <WorkshopServiceRecordOverviewCard workshopId={workshopId} autoFocus={focusInput} setFocusInput={setFocusInput} />
      <WorkshopServiceHistoryOverviewCard workshopId={workshopId} autoFocus={focusInput} setFocusInput={setFocusInput} />
      <WorkshopDataUpdateRequestCard workshopId={workshopId} />
      <ContentCard
        header={t('workshop:accounts.header')}
        headerAction={
          canAddAccount ? (
            <Button variant="primary" size="sm" onClick={() => setIsAddAccountModalOpen(true)} data-test-id="add-account-button">
              {t('workshop:add_account.button')}
            </Button>
          ) : undefined
        }
        isLoading={workshopQuery.isLoading}
      >
        <AccountsTable workshopId={workshopId as any as string} />
      </ContentCard>
      {canAddAccount && (
        <AddAccountModal
          workshopId={workshopId}
          workshopCountry={workshopQuery.data?.country}
          isOpen={isAddAccountModalOpen}
          onClose={() => setIsAddAccountModalOpen(false)}
        />
      )}
      <DangerZoneCard workshopId={workshopId} />
      <ActivityLogCard
        activities={workshopQuery.data?.activities}
        queryKey={useWorkshopQuery.getKey(workshopId)}
        resourceKey={resourceKey}
        workshopName={workshopQuery.data?.name}
        account_id={workshopId}
      />
    </ContentLayout>
  );
};
export default WorkshopDetailsPage;
